import { faDatabase, faPlus, faCodeBranch, faLandmark, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch } from "Store/hooks";
import { Button, Dropdown, Input, Popconfirm, Spin } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { setCurrentTenant, setIsModalOpen, setIsSelectiveUpgradeModalOpen } from "../redux/slice";
import { initialValues } from "../addEditTenant/intialValues";
import { TenantConstants } from "Constants";
import "Features/Tenants/tenants.index.css";
import {
  upgradeLandlordDatabase,
    upgradeReposForAllTenants,
    upgradeRepoDataForAllTenants,
} from "../redux/thunks";
import { setErrorState } from "Components/Basic/ErrorBoundary/redux/slice";
import { useState } from "react";

interface TenantToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
}

const TenantToolbar = ({ searchText, onSearchChange }: TenantToolbarProps) => {
  const dispatch = useAppDispatch();
  const [isRepositoryLoading, setIsRepositoryLoading] = useState(false);
  const [isRepositoryDataLoading, setIsRepositoryDataLoading] = useState(false);
  const [isLandlordDatabaseLoading, setIsLandlordDatabaseLoading] =
    useState(false);

  const upgradeRepositoryDataForAllTenants = async () => {
    setIsRepositoryDataLoading(true);
    try {
      await dispatch(upgradeRepoDataForAllTenants()).unwrap();
      dispatch(
        setErrorState({
          message: "Success",
          description: "Repository default data upgraded successfully!",
          placement: "topRight",
          type: "success",
        })
      );
    } catch (ex: any) {
      dispatch(
        setErrorState({
          message: "Error",
          description: ex?.message,
          placement: "topRight",
          type: "error",
        })
      );
    } finally {
      setIsRepositoryDataLoading(false);
    }
  };

  const upgradeRepositoryForAllTenants = async () => {
    setIsRepositoryLoading(true);
    try {
      await dispatch(upgradeReposForAllTenants()).unwrap();
      dispatch(
        setErrorState({
          message: "Success",
          description: "Repository upgraded successfully!",
          placement: "topRight",
          type: "success",
        })
      );
    } catch (ex: any) {
      dispatch(
        setErrorState({
          message: "Error",
          description: ex?.message,
          placement: "topRight",
          type: "error",
        })
      );
    } finally {
      setIsRepositoryLoading(false);
    }
  };

  const upgradeLandlordDatabaseAction = async () => {
    setIsLandlordDatabaseLoading(true);
    try {
      await dispatch(upgradeLandlordDatabase()).unwrap();
      dispatch(
        setErrorState({
          message: "Success",
          description: "Landlord database upgraded successfully!",
          placement: "topRight",
          type: "success",
        })
      );
    } catch (ex: any) {
      dispatch(
        setErrorState({
          message: "Error",
          description: ex?.message,
          placement: "topRight",
          type: "error",
        })
      );
    } finally {
      setIsLandlordDatabaseLoading(false);
    }
  };

  const isAnyUpgradeLoading =
    isRepositoryLoading || isRepositoryDataLoading || isLandlordDatabaseLoading;

  const upgradeMenuItems = [
    {
      key: "flows",
      onClick: () => dispatch(setIsSelectiveUpgradeModalOpen(true)),
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faCodeBranch} />
          <span>Upgrade Flows for Tenants</span>
        </div>
      ),
    },
    {
      key: "repository",
      label: (
        <Popconfirm
          title="Are you sure?"
          description="Do you want to upgrade the repository of all tenants?"
          onConfirm={upgradeRepositoryForAllTenants}
          okText="Yes"
          cancelText="No"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isRepositoryLoading ? <Spin size="small" /> : <FontAwesomeIcon icon={faDatabase} />}
            <span>{isRepositoryLoading ? "Upgrading..." : "Upgrade Repository for All Tenants"}</span>
          </div>
        </Popconfirm>
      ),
      disabled: isRepositoryLoading,
    },
    {
      key: "repositoryData",
      label: (
        <Popconfirm
          title="Are you sure?"
          description="Do you want to upgrade repository default data (prompts, etc.) for all tenants?"
          onConfirm={upgradeRepositoryDataForAllTenants}
          okText="Yes"
          cancelText="No"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isRepositoryDataLoading ? <Spin size="small" /> : <FontAwesomeIcon icon={faFileAlt} />}
            <span>{isRepositoryDataLoading ? "Upgrading..." : "Upgrade Repository Data"}</span>
          </div>
        </Popconfirm>
      ),
      disabled: isRepositoryDataLoading,
    },
    {
      key: "landlord",
      label: (
        <Popconfirm
          title="Are you sure?"
          description="Do you want to upgrade the landlord database?"
          onConfirm={upgradeLandlordDatabaseAction}
          okText="Yes"
          cancelText="No"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isLandlordDatabaseLoading ? <Spin size="small" /> : <FontAwesomeIcon icon={faLandmark} />}
            <span>{isLandlordDatabaseLoading ? "Upgrading..." : "Upgrade Landlord Database"}</span>
          </div>
        </Popconfirm>
      ),
      disabled: isLandlordDatabaseLoading,
    },
  ];

  return (
    <div
      style={{
        justifyContent: "space-between",
        display: "flex",
        flexDirection: "row-reverse",
        margin: "5px",
        alignItems: "center",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Input
          placeholder="Search tenants..."
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          allowClear
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ width: 260 }}
        />
        <Dropdown menu={{ items: upgradeMenuItems }} trigger={["click"]}>
          <Button type="primary" className="add-tenant-button">
            {isAnyUpgradeLoading ? <Spin size="small" style={{ marginRight: 8 }} /> : null}
            {isAnyUpgradeLoading ? "Upgrading..." : "Upgrade"}
            <DownOutlined style={{ marginLeft: 6 }} />
          </Button>
        </Dropdown>
        <Button
          type="primary"
          className="add-tenant-button"
          onClick={() => {
            dispatch(setCurrentTenant(initialValues));
            dispatch(setIsModalOpen(true));
          }}
        >
          <FontAwesomeIcon style={{ marginRight: "10px" }} icon={faPlus} />
          {TenantConstants.ADD_TENANT}
        </Button>
      </div>
      <div className="tenant-heading">Tenants</div>
    </div>
  );
};

export default TenantToolbar;
